import { jsx as _jsx } from "react/jsx-runtime";
import { PROMO_IMAGE_TITLE_SCALE, resolveTypeScale, } from "@diality/contracts";
import { PromoImage } from "../components/blocks/promo-image";
import { resolveSlotMedia } from "../render/slot-media";
/**
 * PromoImageBlock — the authorable form of the marketing PromoImage.
 *
 * It RENDERS the marketing component rather than restating its layout. The
 * design is finished; a second copy of a 140-line absolutely-positioned band is
 * a copy that drifts, and the whole point of making this authorable is that an
 * Editor gets the band a visitor already gets.
 *
 * ART DIRECTION USES THE BLOCK CONTAINER, just like the rest of the layout.
 * Both images are rendered and mutually excluded at the 900px container step,
 * so a 390px CMS frame inside a wide browser receives the mobile composition
 * and a full-width public page keeps the same breakpoint.
 *
 * THE CALL TO ACTION IS ALWAYS A LINK here. Both halves are required
 * (`validatePromoImage`) and neither `ctaDisabled` nor `onCtaClick` is
 * serializable, so this adapter passes neither: the component's button branch
 * exists for the code-composed Product page, which has no destination yet, and
 * is unreachable from stored content. Do not delete it on that evidence.
 *
 * BOTH HALVES OR NEITHER, exactly as `PromoView` and `HeroMediaCenteredView`
 * decide it: a draft carrying a label and no destination shows no call to action
 * rather than a button that goes nowhere. The validator refuses to SAVE such a
 * band; this guard is what a half-filled draft looks like before it is saved.
 *
 * THE CALL TO ACTION CARRIES THE AR GLYPH whatever its destination, because the
 * design has it and this story does not redesign it. Named rather than worked
 * around: suppressing it would need `ctaIcon={false}`, since the component's
 * `ctaIcon ?? <ArViewerIcon/>` treats `null` as "not supplied" — and that would
 * make an authored band look different from the designed one. A per-destination
 * glyph is a field and a story of its own.
 *
 * NO ALT TEXT FIELD. The five authored fields are the five the design has. A
 * picture's description belongs to the ASSET (`edit-media-asset-metadata` owns
 * it) and no resolver puts it on the rendered picture yet — the hole
 * `art-direction-per-screen` already records. Until it does, the artwork is
 * decorative: the heading carries the message and labels the section, so an
 * empty description is the honest one rather than a missing one.
 */
export function PromoImageBlock({ data }) {
    const { title, cta, desktop, mobile } = data;
    const hasCallToAction = Boolean(cta?.label && cta?.href);
    return (_jsx(PromoImage, { title: title, 
        // Resolved here as well as in the mapper, so the block is safe to render
        // from data composed in code that never passed through a stored block.
        titleSize: resolveTypeScale(PROMO_IMAGE_TITLE_SCALE, data.titleSize), imageSrc: desktop?.url ?? "", mobileImageSrc: mobile?.url, imageAlt: desktop?.alt ?? "", ctaLabel: hasCallToAction ? cta.label : undefined, ctaHref: hasCallToAction ? cta.href : undefined }));
}
export function promoImageFromBlock(block, resolve) {
    const scalars = block.scalars ?? {};
    return {
        // NOT trimmed. The heading is `white-space: pre-line` and the Editor's line
        // breaks are content; trimming here would silently edit the design.
        title: scalars.title ?? "",
        // A band stored before the size control existed has no such scalar, so it
        // resolves to the default — which is the 80px it has always drawn.
        titleSize: resolveTypeScale(PROMO_IMAGE_TITLE_SCALE, scalars.titleSize),
        cta: {
            label: scalars.ctaLabel?.trim() ?? "",
            href: scalars.ctaHref?.trim() ?? "",
        },
        // Each screen resolved from its OWN slot. Both slots are single-target, so
        // each of these is one framing and resolveSlotMedia's multi-target
        // `sources` branch is never taken — deriving one screen's artwork from the
        // other's reference is exactly what this component must not do.
        desktop: resolveSlotMedia(block, "desktopImage", resolve),
        mobile: resolveSlotMedia(block, "mobileImage", resolve),
    };
}
export const promoImageBlockEntry = {
    Component: PromoImageBlock,
    fromBlock: promoImageFromBlock,
};
//# sourceMappingURL=promo-image.js.map